<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Check, ChevronDown } from 'lucide-vue-next';

interface Country {
  name: string;
  code: string;
  flag: string;
  dialCode: string;
}

const props = withDefaults(
  defineProps<{
    modelValue?: any;
    placeholder?: string;
    class?: any;
    disabled?: boolean;
    code?: string;
  }>(),
  {
    modelValue: '',
    placeholder: 'Enter phone number',
    disabled: false,
    code: 'DE',
  }
);

const emit = defineEmits<{
  'update:modelValue': [value: string];
}>();

// Common countries list
const countries: Country[] = [
  { name: 'United States', code: 'US', flag: '🇺🇸', dialCode: '+1' },
  { name: 'United Kingdom', code: 'GB', flag: '🇬🇧', dialCode: '+44' },
  { name: 'Germany', code: 'DE', flag: '🇩🇪', dialCode: '+49' },
  { name: 'France', code: 'FR', flag: '🇫🇷', dialCode: '+33' },
  { name: 'Canada', code: 'CA', flag: '🇨🇦', dialCode: '+1' },
  { name: 'Australia', code: 'AU', flag: '🇦🇺', dialCode: '+61' },
  { name: 'Japan', code: 'JP', flag: '🇯🇵', dialCode: '+81' },
  { name: 'China', code: 'CN', flag: '🇨🇳', dialCode: '+86' },
  { name: 'India', code: 'IN', flag: '🇮🇳', dialCode: '+91' },
  { name: 'Brazil', code: 'BR', flag: '🇧🇷', dialCode: '+55' },
  { name: 'Spain', code: 'ES', flag: '🇪🇸', dialCode: '+34' },
  { name: 'Italy', code: 'IT', flag: '🇮🇹', dialCode: '+39' },
  { name: 'Netherlands', code: 'NL', flag: '🇳🇱', dialCode: '+31' },
  { name: 'Sweden', code: 'SE', flag: '🇸🇪', dialCode: '+46' },
  { name: 'Norway', code: 'NO', flag: '🇳🇴', dialCode: '+47' },
  { name: 'Denmark', code: 'DK', flag: '🇩🇰', dialCode: '+45' },
  { name: 'Finland', code: 'FI', flag: '🇫🇮', dialCode: '+358' },
  { name: 'Switzerland', code: 'CH', flag: '🇨🇭', dialCode: '+41' },
  { name: 'Austria', code: 'AT', flag: '🇦🇹', dialCode: '+43' },
  { name: 'Belgium', code: 'BE', flag: '🇧🇪', dialCode: '+32' },
];

const selectedCountry = ref<Country>(countries.find(c => c.code === props.code) || countries[0]);
const phoneNumber = ref('');
const isOpen = ref(false);

// Get the full phone number with country code
const getFullPhoneNumber = () => {
  if (!phoneNumber.value) return '';
  return `${selectedCountry.value.dialCode}${phoneNumber.value}`;
};

// Parse the modelValue to extract country and phone number
const parsePhoneValue = (value: string) => {
  if (!value) return;

  // Find country by dial code
  const country = countries.find(c => value.startsWith(c.dialCode));
  if (country) {
    selectedCountry.value = country;
    phoneNumber.value = value.substring(country.dialCode.length);
  }
};

// Watch for external changes to modelValue
watch(
  () => props.modelValue,
  newValue => {
    if (newValue !== getFullPhoneNumber()) {
      parsePhoneValue(newValue || '');
    }
  },
  { immediate: true }
);

// Update the model value when phone number or country changes
watch([phoneNumber, selectedCountry], () => {
  const fullNumber = getFullPhoneNumber();
  emit('update:modelValue', fullNumber);
});

const handleCountrySelect = (country: Country) => {
  selectedCountry.value = country;
  isOpen.value = false;
};

const formatPhoneNumber = (value: string) => {
  // Basic formatting - remove all non-digits
  return value.replace(/\D/g, '');
};

const handlePhoneInput = (event: Event) => {
  const target = event.target as HTMLInputElement;
  const formatted = formatPhoneNumber(target.value);
  phoneNumber.value = formatted;
};
</script>

<template>
  <div class="tw-flex tw-gap-1">
    <!-- Country Selector -->
    <Popover v-model:open="isOpen">
      <PopoverTrigger as-child>
        <Button
          variant="outline"
          role="combobox"
          :aria-expanded="isOpen"
          :disabled="disabled"
          class="tw-w-[120px] tw-justify-between tw-h-12 tw-px-3 tw-border tw-border-secondary-200 tw-rounded-md tw-bg-secondary-50 focus:tw-border-secondary-500"
        >
          <span class="tw-flex tw-items-center tw-gap-2">
            <span>{{ selectedCountry.flag }}</span>
            <span class="tw-text-sm">{{ selectedCountry.dialCode }}</span>
          </span>
          <ChevronDown class="tw-ml-2 tw-h-4 tw-w-4 tw-shrink-0 tw-opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent class="tw-w-[300px] tw-p-0">
        <Command>
          <CommandInput placeholder="Search country..." />
          <CommandList>
            <CommandEmpty>No country found.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                v-for="country in countries"
                :key="country.code"
                :value="country.name"
                @select="handleCountrySelect(country)"
              >
                <Check
                  :class="[
                    'tw-mr-2 tw-h-4 tw-w-4',
                    selectedCountry.code === country.code ? 'tw-opacity-100' : 'tw-opacity-0',
                  ]"
                />
                <span class="tw-mr-2">{{ country.flag }}</span>
                <span class="tw-mr-2">{{ country.dialCode }}</span>
                <span>{{ country.name }}</span>
              </CommandItem>
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>

    <!-- Phone Number Input -->
    <Input
      :model-value="phoneNumber"
      @input="handlePhoneInput"
      :placeholder="placeholder"
      :disabled="disabled"
      :class="
        cn(
          'tw-flex-1 tw-h-12 tw-px-3 tw-border tw-border-secondary-200 tw-rounded-md tw-bg-secondary-50 tw-text-secondary-600 focus:tw-bg-white focus:tw-border-secondary-500 focus:tw-outline-none',
          props.class
        )
      "
    />
  </div>
</template>
